import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import React, { useState } from "react";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { useSearchParams, useNavigate } from "react-router-dom";

const BriefcaseIcon = ({ active }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={active ? "#4A90D9" : "#94a3b8"}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <path d="M2 12h20" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#4A90D9"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

const Activities = ({ activityData, loading, onItemClick }) => {
  const [searchParams] = useSearchParams();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const nav = useNavigate();

  const isDeleteActivity = (item) => {
    const comment = item?.comment?.toLowerCase() || "";
    return comment.includes("delete") || comment.includes("deleted");
  };
  const canRedirect = (item) => item?.redirection === 1;

  // ── Handles both APIs ──
  // MyDashboard: { item_id, type, comment, date, title }
  // Dashboard:   { id, joborder_id, title, comment, date }
  const getRedirectLink = (item) => {
    const comment = item?.comment || "";
    const type = item?.type || "";

    // ── Dashboard API: joborder_id hoga toh direct job page ──
    if (item?.joborder_id) return `/jobOrders/${item.joborder_id}`;

    // ── MyDashboard API: item_id + type hoga ──
    const id = item?.item_id || item?.id || null;
    if (!id) return null;

    if (type === "Job") return `/jobOrders/${id}`;
    if (type === "Company") return `/CompanyDetails/${id}?tab=companyInfo`;
    if (type === "User") return `/UsersDetails/${id}?tab=b_info`;
    if (type === "Branch") {
      const companyId = item?.company_id;
      if (companyId && id) {
        return `/CompanyDetails/${companyId}/BranchDetails/${id}?tab=BranchInfo`;
      }
      return null;
    }

    if (type === "Candidate") {
      if (comment.includes("applied") || comment.includes("shortlisted"))
        return `/Manage-Candidates/${id}?tab=2`;
      else if (comment.includes("created"))
        return `/Manage-Candidates/${id}?tab=0`;
      else if (comment.includes("Status"))
        return `/Manage-Candidates/${id}?tab=0`;
      return `/Manage-Candidates/${id}?tab=0`;
    }

    if (comment.toLowerCase().includes("company"))
      return `/CompanyDetails/${id}?tab=companyInfo`;
    if (comment.toLowerCase().includes("branch"))
      return `/CompanyDetails/${id}?tab=companyBranch&page=1`;

    return null;
  };

  const handleRowClick = (item) => {
    if (isDeleteActivity(item) || !canRedirect(item)) return;
    const path = getRedirectLink(item);
    if (path) nav(path);
    onItemClick?.(item);
  };

  const getIcon = (comment, type, isHovered) => {
    if (searchParams?.get("data") === "Jobs" || type === "Job") {
      return <BriefcaseIcon active={isHovered} />;
    } else {
      if (comment?.includes("applied")) {
        return (
          <PersonOutlineOutlinedIcon
            sx={{
              fontSize: "20px",
              color: isHovered ? "#006CB7" : "#6A7885",
              transition: "color 0.25s",
            }}
          />
        );
      } else if (type === "Company") {
        return (
          <WorkHistoryOutlinedIcon
            sx={{ fontSize: "20px", color: isHovered ? "#006CB7" : "#6A7885" }}
          />
        );
      } else if (comment?.includes("created")) {
        return (
          <PersonAddAlt1OutlinedIcon
            sx={{
              fontSize: "20px",
              color: isHovered ? "#006CB7" : "#6A7885",
              transition: "color 0.25s",
            }}
          />
        );
      } else {
        return (
          <WorkHistoryOutlinedIcon
            sx={{
              fontSize: "20px",
              color: isHovered ? "#006CB7" : "#6A7885",
              transition: "color 0.25s",
            }}
          />
        );
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!activityData || activityData?.length === 0) {
    return (
      <Typography
        variant="body1"
        color="textSecondary"
        sx={{
          fontWeight: 700,
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%,-50%)",
        }}
      >
        No activities
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {activityData?.map(
        (item, index) =>
          item?.comment !== null && (
            <Box
              key={index}
              onClick={() => handleRowClick(item)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              sx={{
                display: "flex",
                height: "80px",
                alignItems: "center",
                gap: "12px",
                borderRadius: "12px",
                padding: "10px 12px",
                boxShadow:
                  hoveredIndex === index
                    ? "0 2px 10px rgba(0,0,0,0.07)"
                    : "none",
                cursor:
                  isDeleteActivity(item) || !canRedirect(item)
                    ? "default"
                    : "pointer",
                transition: "background 0.25s, box-shadow 0.25s",
                position: "relative",
                overflow: "hidden",
                backgroundColor: hoveredIndex === index ? "#F1F5F9" : "#fff",
              }}
            >
              {/* Icon Box */}
              <Box
                sx={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "10px",
                  border: `1px solid ${hoveredIndex === index ? "#fff" : "#F1F5F9"}`,
                  background: "#fff",
                  boxShadow:
                    hoveredIndex === index
                      ? "0 4px 4px 0 rgba(0,0,0,0.08)"
                      : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transform:
                    hoveredIndex === index
                      ? "translateX(5px)"
                      : "translateX(0px)",
                  transition:
                    "transform 0.5s ease, background 0.25s, border-color 0.25s",
                  willChange: "transform",
                }}
              >
                {getIcon(item?.comment, item?.type, hoveredIndex === index)}
              </Box>

              {/* Text */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  transform:
                    hoveredIndex === index
                      ? "translateX(5px)"
                      : "translateX(0px)",
                  transition: "transform 0.25s ease",
                  willChange: "transform",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 400,
                    fontSize: "12px",
                    color: "#6A7885",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    mb: "1px",
                  }}
                >
                  {item?.title?.charAt(0).toUpperCase() +
                    item?.title?.slice(1).toLowerCase()}{" "}
                  {"|"} {new Date(item?.date).toLocaleString()}
                </Typography>

                <Tooltip title={item?.comment || ""} arrow placement="top">
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      fontSize: "16px",
                      color: "#212121",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item?.comment}
                  </Typography>
                </Tooltip>
              </Box>

              {/* Arrow */}
              {!isDeleteActivity(item) && canRedirect(item) && (
                <Box
                  sx={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "8px",
                    border: "1px solid #cce0f5",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    opacity: hoveredIndex === index ? 1 : 0,
                    transform:
                      hoveredIndex === index
                        ? "translateX(-5px)"
                        : "translateX(10px)",
                    transition: "transform 0.25s ease, opacity 0.25s ease",
                    willChange: "transform, opacity",
                    pointerEvents: "none",
                  }}
                >
                  <ArrowIcon />
                </Box>
              )}
            </Box>
          ),
      )}
    </Box>
  );
};

export default Activities;
